import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Container, Card, CardBody, CardTitle, CardText, Button, Table, Input, Label } from "reactstrap";
import { VideoProcessorAPI } from "../../helpers/firebase_helper";
import { toast } from "react-toastify";


const Summary = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const summary = location.state || {};
  const [loading, setLoading] = useState(false);
  const isViewConfiguration = summary.isViewConfiguration || false;
// console.log("summary",summary);
  const [tags, setTags] = useState([]);

  // Extract data from new payload structure or use old structure for backward compatibility
  const payload = summary.payload || {};
  const configuration = payload.configuration || {};
  const transcodeVideoConfig = configuration.TranscodeVideo || {};
  const customSettings = transcodeVideoConfig.CustomSettings || {};
  const hlsConfig = customSettings.Hls?.config || [];
  const dashConfig = customSettings.Dash?.config || [];

  // Extract hlsResolutions and dashResolutions from new structure or use old structure
  const hlsResolutions = summary.hlsResolutions || hlsConfig.map(item => ({
    resolution: item.Resolution,
    codec: item.Codec,
    height: item.Height,
    width: item.Width,
    bitrate: item.MaxBitrate || item.Bitrate,
    rateControlMode: item.RateControlMode,
    audioDescriptions: item.AudioDescriptions || {}
  })) || [];

  const dashResolutions = summary.dashResolutions || dashConfig.map(item => ({
    resolution: item.Resolution,
    codec: item.Codec,
    height: item.Height,
    width: item.Width,
    bitrate: item.MaxBitrate || item.Bitrate,
    rateControlMode: item.RateControlMode,
    audioDescriptions: item.AudioDescriptions || {}
  })) || [];

  // Extract audio descriptions
  const hlsAudioDescriptions = hlsResolutions.map(item => item.audioDescriptions || {});
  const dashAudioDescriptions = dashResolutions.map(item => item.audioDescriptions || {});

  // Check if default settings are enabled
  const isDefaultSetting = transcodeVideoConfig.DefaultSettings || false;
  const enableHls = customSettings.Hls?.Enabled !== false;
  const enableDash = customSettings.Dash?.Enabled !== false;

  // Extract thumbnail, overlay, and captions settings from new structure or use old structure
  const thumbnailConfig = configuration.Thumbnail || {};
  const overlayConfig = configuration.ImageOverlay || {};
  const captionsConfig = configuration.Captions || {};
  const translateWebCaptions = captionsConfig.TranslateWebCaptions || {};

  const thumbnailSettings = summary.thumbnailSettings || {
    enabled: thumbnailConfig.Enabled || false,
    width: '',
    height: '',
    maxCapture: ''
  };

  const overlaySettings = summary.overlaySettings || {
    enabled: overlayConfig.Enabled || false,
    offsetTop: overlayConfig.TopOffset || '',
    offsetLeft: overlayConfig.LeftOffset || '',
    width: overlayConfig.Width || '',
    height: overlayConfig.Height || '',
    opacity: overlayConfig.Opacity || '',
    imageUrl: overlayConfig.ImageUrl || '',
    startTime: '' // Not in new structure
  };

  const captionsSettings = summary.captionsSettings || {
    enableCaptions: captionsConfig.Enabled || false,
    enableTranslation: translateWebCaptions.Enabled || false,
    captionFormat: captionsConfig.SrtCaptions && captionsConfig.VttCaptions 
      ? 'WebVTT and  SRT' 
      : captionsConfig.SrtCaptions 
        ? 'SRT' 
        : captionsConfig.VttCaptions 
          ? 'WebVTT' 
          : '',
    sourceLanguage: translateWebCaptions.SourceLanguageCode || '',
    languages: translateWebCaptions.TargetLanguageCodes || []
  };

  // Mapping function to convert language names to codes
  const getLanguageCode = (languageName) => {
    const languageMap = {
      'English': 'en',
      'Hindi': 'hi',
      'Spanish': 'es',
      'French': 'fr',
      'German': 'de',
      'Chinese': 'zh',
      'Arabic': 'ar',
      'Japanese': 'jp',
      'Russian': 'ru',
      'Italian': 'it',
      // Also handle if already a code
      'en': 'en',
      'hi': 'hi',
      'es': 'es',
      'fr': 'fr',
      'de': 'de',
      'zh': 'zh',
      'ar': 'ar',
      'jp': 'jp',
      'ru': 'ru',
      'it': 'it',
    };
    return languageMap[languageName] || languageName; // Return code if found, otherwise return as-is
  };

  // Mapping function to convert language codes to names
  const getLanguageName = (languageCode) => {
    const languageMap = {
      'en': 'English',
      'hi': 'Hindi',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'zh': 'Chinese',
      'ar': 'Arabic',
      'jp': 'Japanese',
      'ru': 'Russian',
      'it': 'Italian',
      // Also handle if already a name
      'English': 'English',
      'Hindi': 'Hindi',
      'Spanish': 'Spanish',
      'French': 'French',
      'German': 'German',
      'Chinese': 'Chinese',
      'Arabic': 'Arabic',
      'Japanese': 'Japanese',
      'Russian': 'Russian',
      'Italian': 'Italian',
    };
    return languageMap[languageCode] || languageCode; // Return name if found, otherwise return as-is
  };

  // Convert array of language names to codes
  const convertLanguagesToCodes = (languages) => {
    if (!languages || !Array.isArray(languages)) return [];
    return languages.map(lang => getLanguageCode(lang)).filter(Boolean);
  };

  // Normalize caption format to handle case variations
  const normalizeCaptionFormat = (format) => {
    if (!format) return '';
    const normalized = format.toLowerCase().trim();
    if (normalized === 'srt') return 'SRT';
    if (normalized === 'webvtt' || normalized === 'vtt') return 'WebVTT';
    if (normalized.includes('both') || normalized.includes('and')) return 'WebVTT and  SRT';
    return format; // Return original if no match
  };
  
  const handlePrevious = () => {
    navigate(-1);
  };

  const transformPayload = (inputPayload) => {
    // If payload already exists (new structure), use it directly
    if (inputPayload.payload) {
      return inputPayload.payload;
    }
    
    // Otherwise, transform from old structure
    const hasCustomSettings =
    (inputPayload.hlsResolutions && inputPayload.hlsResolutions.length > 0) || 
    (inputPayload.dashResolutions && inputPayload.dashResolutions.length > 0);
     
    return {
      filename: inputPayload.video.filename,
      file_id: inputPayload.video.file_id,
      file_url: inputPayload.video.cdn_url || "",
      configuration: {
        ThumbnailPosition: inputPayload.thumbnailSettings.enabled ? "10" : "0",
  
        PreprocessVideo: {
          Mediainfo: {
            Enabled: true
          },
          Thumbnail: {
            Enabled: inputPayload.thumbnailSettings.enabled,
            ThumbnailPosition: inputPayload.thumbnailSettings.enabled ? "10" : "0"
          }
        },
  
        AnalyzeVideo: {
          TranscribeVideo: {
            Enabled: inputPayload.captionsSettings.enableCaptions,
            MediaType: "Audio",
            TranscribeLanguage: "en-US"
          },
          TranscodeVideo: {
            Enabled: true,
            MediaType: "Video",
            DefaultSettings: !hasCustomSettings,
            CustomSettings: {
              HLS: (inputPayload.hlsResolutions || []).map(res => ({
                resolution: res.resolution,
                codec: res.codec,
                height: res.height,
                width: res.width,
                bitrate: res.bitrate
              })),
              DASH: (inputPayload.dashResolutions || []).map(res => ({
                resolution: res.resolution,
                codec: res.codec,
                height: res.height,
                width: res.width,
                bitrate: res.bitrate
              }))
            }
          }
        },
  
        TransformText: {
          PollyWebCaptions: {
            Enabled: false,
            MediaType: "MetadataOnly",
            SourceLanguageCode: "en"
          },
          WebToSRTCaptions: {
            Enabled: inputPayload.captionsSettings.enableCaptions && 
                    (normalizeCaptionFormat(inputPayload.captionsSettings.captionFormat) === "SRT" || 
                     normalizeCaptionFormat(inputPayload.captionsSettings.captionFormat) === "WebVTT and  SRT"),
            MediaType: "MetadataOnly",
            TargetLanguageCodes: convertLanguagesToCodes(inputPayload.captionsSettings.languages)
          },
          WebToVTTCaptions: {
            Enabled: inputPayload.captionsSettings.enableCaptions && 
                    (normalizeCaptionFormat(inputPayload.captionsSettings.captionFormat) === "WebVTT" || 
                     normalizeCaptionFormat(inputPayload.captionsSettings.captionFormat) === "WebVTT and  SRT"),
            MediaType: "MetadataOnly",
            TargetLanguageCodes: convertLanguagesToCodes(inputPayload.captionsSettings.languages)
          }
        },
  
        Translate: {
          Translate: {
            Enabled: false,
            MediaType: "Text"
          },
          TranslateWebCaptions: {
            Enabled: inputPayload.captionsSettings.enableTranslation,
            MediaType: "MetadataOnly",
            ParallelDataNames: [],
            SourceLanguageCode: "en",
            TargetLanguageCodes: ["hi", "ar"],
            TerminologyNames: []
          }
        },
  
        WebCaptions: {
          WebCaptions: {
            Enabled: true,
            MediaType: "MetadataOnly",
            SourceLanguageCode: "en-US"
          }
        }
      }
    };
  };
  
  const handleSubmit = async(e) => {
    // console.log("Summary",summary);
    
    // Use payload directly if it exists (new structure), otherwise transform from old structure
    const apiPayload = summary.payload || transformPayload(summary);
    console.log("API Payload:", apiPayload);
    
    setLoading(true);
    // Make API call to submit the payload
    try {
      const res = await VideoProcessorAPI.processVideo(apiPayload);
      // console.log("API Response:", res);
      if (res.status === "success") {
        toast.success("Video processing job submitted successfully!");
        navigate("/video-processor");
      } else {
        toast.error("Failed to submit video processing job.");
      }
      setLoading(false);
    } catch (error) {
      console.error("API Error:", error);
      setLoading(false);
    }
  };
  document.title = isViewConfiguration ? "Media Pulse | View Configuration" : "Media Pulse | Video Processor";

  return (
    <div className="page-content">
      <Container fluid>

        <BreadCrumb title={isViewConfiguration ? "View Configuration" : "Summary"} pageTitle="Video Processor" />

        {/* Cards Section */}
        <div className="row mt-4">
          <div className="row align-items-stretch">
            <div className="col-md-6 d-flex flex-column">
              <Card className="mb-4">
                <CardBody>
                  <CardTitle tag="h5" style={{ color: "#6691e7" }}>Job Details</CardTitle>
                  <hr />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <CardText>
                      <b>Job Name:</b> {summary.jobName}
                    </CardText>
                    <CardText>
                      <b>Workflow Type:</b> {summary.workflow}
                    </CardText>
                  </div>
                </CardBody>
              </Card>

              {/* Transcode Video Card */}
              <Card className="mb-4">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center">
                    <CardTitle tag="h5" style={{ color: "#6691e7" }}>Transcode Video</CardTitle>
                    <div className="form-check form-switch">
                      <Input
                        className="form-check-input"
                        type="checkbox"
                        id="defaultSettingDisplay"
                        checked={isDefaultSetting}
                        disabled
                      />
                      <Label className="form-check-label text-muted ms-2" htmlFor="defaultSettingDisplay">
                        Default Setting
                      </Label>
                    </div>
                  </div>
                  <hr />
                </CardBody>
                <div className="container">
                  {/* HLS Group */}
                  {enableHls && (
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <h5>HLS Group</h5>
                      </div>
                      <Table striped hover className="align-middle table-nowrap mb-0 mt-3">
                        <thead className="text-center text-muted table-light">
                          <tr>
                            <th scope="col">Resolution</th>
                            <th scope="col">Width</th>
                            <th scope="col">Height</th>
                            <th scope="col">Codec</th>
                            <th scope="col">RateControlMode</th>
                            <th scope="col">MaxBitrate / Bitrate</th>
                          </tr>
                        </thead>
                        <tbody className="text-center">
                          {hlsResolutions && hlsResolutions.length > 0 ? (
                            hlsResolutions.map((row, index) => (
                              <tr key={`HLS-${index}`} className="border-bottom">
                                <td>{row.resolution}</td>
                                <td>{row.width}</td>
                                <td>{row.height}</td>
                                <td>{row.codec === "H_264" ? "H.264" : row.codec}</td>
                                <td>{row.rateControlMode}</td>
                                <td>{row.bitrate}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center text-muted">No HLS resolutions configured</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                      
                      {/* HLS Audio Description Table */}
                      {hlsResolutions && hlsResolutions.length > 0 && (
                        <div className="mt-4">
                          <h6 className="mb-3">Audio Description</h6>
                          <Table striped hover className="align-middle table-nowrap mb-0">
                            <thead className="text-muted table-light">
                              <tr>
                                <th scope="col">Codec</th>
                                <th scope="col">Bitrate</th>
                                <th scope="col">SampleRate</th>
                              </tr>
                            </thead>
                            <tbody>
                              {hlsAudioDescriptions.map((audio, index) => (
                                <tr key={`HLS-Audio-${index}`}>
                                  <td>{audio.Codec || audio.codec || ''}</td>
                                  <td>{audio.Bitrate || audio.bitrate || ''}</td>
                                  <td>{audio.SampleRate || audio.sampleRate || ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* DASH Group */}
                  {enableDash && (
                    <div className="mt-5 mb-4">
                      <hr />
                      <div className="d-flex justify-content-between align-items-center">
                        <h5>DASH Group</h5>
                      </div>
                      <Table striped hover className="align-middle table-nowrap mb-0 mt-3">
                        <thead className="text-center text-muted table-light">
                          <tr>
                            <th scope="col">Resolution</th>
                            <th scope="col">Width</th>
                            <th scope="col">Height</th>
                            <th scope="col">Codec</th>
                            <th scope="col">RateControlMode</th>
                            <th scope="col">MaxBitrate / Bitrate</th>
                          </tr>
                        </thead>
                        <tbody className="text-center">
                          {dashResolutions && dashResolutions.length > 0 ? (
                            dashResolutions.map((row, index) => (
                              <tr key={`DASH-${index}`} className="border-bottom">
                                <td>{row.resolution}</td>
                                <td>{row.width}</td>
                                <td>{row.height}</td>
                                <td>{row.codec === "H_264" ? "H.264" : row.codec}</td>
                                <td>{row.rateControlMode}</td>
                                <td>{row.bitrate}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center text-muted">No DASH resolutions configured</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                      
                      {/* DASH Audio Description Table */}
                      {dashResolutions && dashResolutions.length > 0 && (
                        <div className="mt-4">
                          <h6 className="mb-3">Audio Description</h6>
                          <Table striped hover className="align-middle table-nowrap mb-0">
                            <thead className="text-muted table-light">
                              <tr>
                                <th scope="col">Codec</th>
                                <th scope="col">Bitrate</th>
                                <th scope="col">SampleRate</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dashAudioDescriptions.map((audio, index) => (
                                <tr key={`DASH-Audio-${index}`}>
                                  <td>{audio.Codec || audio.codec || ''}</td>
                                  <td>{audio.Bitrate || audio.bitrate || ''}</td>
                                  <td>{audio.SampleRate || audio.sampleRate || ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Second Column */}
            <div className="col-md-6 d-flex flex-column">

              <Card className="mb-4">
                <CardBody>
                  <CardTitle tag="h5" style={{ color: "#6691e7" }}>Thumbnail Generation</CardTitle>
                  <hr />
                  <div className="form-check form-switch form-check-right mb-3">
                    <Input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="thumbnailSwitchDisplay"
                      checked={thumbnailSettings.enabled}
                      disabled
                    />
                    <Label className="form-check-label text-muted" htmlFor="thumbnailSwitchDisplay">
                      Thumbnail Generation
                    </Label>
                  </div>
                </CardBody>
              </Card>

              {/* Overlay Image Card */}
              <Card className="mb-4">
                <CardBody>
                  <CardTitle tag="h5" style={{ color: "#6691e7" }}>Overlay Image</CardTitle>
                  <hr />
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check form-switch form-check-right mb-4">
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="overlaySwitchDisplay"
                          checked={overlaySettings.enabled}
                          disabled
                        />
                        <Label className="form-check-label text-muted" htmlFor="overlaySwitchDisplay">
                          Overlay Image
                        </Label>
                      </div>
                    </div>
                    {overlaySettings.imageUrl && (
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label><b>Image URL</b></label>
                          <input type="text" className="form-control"
                            value={overlaySettings.imageUrl}
                            readOnly />
                        </div>
                      </div>
                    )}
                  </div>
                  {overlaySettings.enabled && (
                    <>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label><b>Offset From Top (pixels)</b></label>
                            <input type="number" className="form-control"
                              value={overlaySettings.offsetTop || ''}
                              readOnly />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label><b>Offset From Left (pixels)</b></label>
                            <input type="number" className="form-control"
                              value={overlaySettings.offsetLeft || ''}
                              readOnly />
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label><b>Width</b></label>
                              <input type="number" className="form-control"
                                value={overlaySettings.width || ''}
                                readOnly />
                            </div>
                            <div className="col-md-6 mb-3">
                              <label><b>Height</b></label>
                              <input type="number" className="form-control"
                                value={overlaySettings.height || ''}
                                readOnly />
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label><b>Opacity</b></label>
                            <input type="number" className="form-control"
                              value={overlaySettings.opacity || ''}
                              readOnly />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>

              {/* Captions Card */}
              <Card className="mb-4">
                <CardBody>
                  <CardTitle tag="h5" style={{ color: "#6691e7" }}>Captions</CardTitle>
                  <hr />
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check form-switch form-check-right mb-4">
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="enableCaptionsDisplay"
                          checked={captionsSettings.enableCaptions}
                          disabled
                        />
                        <Label className="form-check-label text-muted" htmlFor="enableCaptionsDisplay">
                          Enable Captions
                        </Label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check form-switch form-check-right mb-4">
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="enableTranslationDisplay"
                          checked={captionsSettings.enableTranslation}
                          disabled
                        />
                        <Label className="form-check-label text-muted" htmlFor="enableTranslationDisplay">
                          Enable Translation
                        </Label>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label><b>Select Caption Format</b></label>
                        <div className="d-flex align-items-center">
                          <div className="form-check me-3">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="captionFormatDisplay"
                              id="webvttOptionDisplay"
                              checked={captionsSettings.captionFormat === "WebVTT"}
                              disabled
                            />
                            <label className="form-check-label" htmlFor="webvttOptionDisplay">
                              WebVTT
                            </label>
                          </div>
                          <div className="form-check me-3">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="captionFormatDisplay"
                              id="srtOptionDisplay"
                              checked={captionsSettings.captionFormat === "SRT"}
                              disabled
                            />
                            <label className="form-check-label" htmlFor="srtOptionDisplay">
                              SRT
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="captionFormatDisplay"
                              id="bothOptionDisplay"
                              checked={captionsSettings.captionFormat === "WebVTT and  SRT"}
                              disabled
                            />
                            <label className="form-check-label" htmlFor="bothOptionDisplay">
                              Both
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label><b>Source Language</b></label>
                        <input type="text" className="form-control"
                          value={getLanguageName(captionsSettings.sourceLanguage) || captionsSettings.sourceLanguage || ''}
                          readOnly />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label><b>Targeted Languages</b></label>
                        <div className="tags mt-2">
                          {captionsSettings.languages && captionsSettings.languages.length > 0 ? (
                            captionsSettings.languages.map((tag) => (
                              <span className="badge me-2 fs-6" style={{ background: '#50c3e6' }} key={tag}>
                                {getLanguageName(tag) || tag}
                              </span>
                            ))
                          ) : (
                            <p className="text-muted">No languages selected.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>

          {/* Save and Cancel Buttons */}
          <div className="d-flex justify-content-end mb-3 me-5">
            {isViewConfiguration ? (
              <Button
                color="secondary" 
                onClick={handlePrevious}
              >
                <i className="bx bx-arrow-back me-1"></i> Back
              
              </Button>
            ) : (
              <>
                <button
                  type="button"
                  style={{ height: '40px', width: '100px', marginRight: "20px" }}
                  className="btn btn-outline-primary"
                  onClick={handlePrevious}
                >
                  Previous
                </button>
                <button
                  type="submit"
                  style={{ height: '40px', width: '100px' }}
                  className="btn btn-success"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit'}
                </button>
              </>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Summary;
